import { Link } from "react-router-dom";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import HowItWorks from "@/pages/create/HowItWorks";
import StepBasics from "@/pages/create/StepBasics";
import StepLogo from "@/pages/create/StepLogo";
import StepWhitepaper from "@/pages/create/StepWhitepaper";
import StepPublish from "@/pages/create/StepPublish";
import useCreateFlow from "@/hooks/useCreateFlow";
import ResumeDraft from "@/components/ResumeDraft";
import { Loader2 } from "lucide-react";

export default function Create() {
  const flow = useCreateFlow();
  const {
    step, next, back, form, setForm, coin, logoUrl, whitepaper, wpTone, setWpTone,
    logoBusy, wpBusy, aiError, submitBusy, publishBusy,
    promoProps, payments, primaryAddress, resuming,
    submitDraft, uploadLogo, genLogo, genWhitepaper, publish, previewOnly,
  } = flow;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="create-page">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {resuming && (
            <div className="border border-[#3a3a3a] bg-[#121212] p-6 mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-[#e2e2e2]" data-testid="resume-loading">
              <Loader2 size={14} className="animate-spin text-[#cfff04]" /> Restoring your draft…
            </div>
          )}
          {step === 0 && !resuming && <ResumeDraft className="mb-8" />}
          {step === 0 && !resuming && <HowItWorks />}

          {/* Progress */}
          <div className="flex items-center gap-2 mb-10">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 transition-colors ${
                  i <= step ? "bg-[#cfff04]" : "bg-[#222]"
                }`}
                data-testid={`step-bar-${i}`}
              />
            ))}
          </div>

          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
            // Step {step + 1} of 4
          </div>

          {step === 0 && !resuming && (
            <StepBasics form={form} setForm={setForm} submitBusy={submitBusy} submitDraft={submitDraft} />
          )}

          {step === 1 && coin && (
            <StepLogo
              coin={coin}
              logoUrl={logoUrl}
              logoBusy={logoBusy}
              aiError={aiError}
              genLogo={genLogo}
              uploadLogo={uploadLogo}
              next={next}
              back={back}
            />
          )}

          {step === 2 && coin && (
            <StepWhitepaper
              coin={coin}
              whitepaper={whitepaper}
              wpBusy={wpBusy}
              aiError={aiError}
              genWhitepaper={genWhitepaper}
              tone={wpTone}
              setTone={setWpTone}
              next={next}
              back={back}
            />
          )}

          {step === 3 && coin && (
            <StepPublish
              coin={coin}
              {...promoProps}
              paymentsHealthy={payments.paymentsHealthy}
              paymentsReason={payments.paymentsReason}
              waitlistEmail={payments.waitlistEmail}
              setWaitlistEmail={payments.setWaitlistEmail}
              waitlistBusy={payments.waitlistBusy}
              waitlistDone={payments.waitlistDone}
              joinWaitlist={payments.joinWaitlist}
              publish={publish}
              publishBusy={publishBusy}
              previewOnly={previewOnly}
              back={back}
              primaryAddress={primaryAddress}
            />
          )}

          <div className="mt-16 pt-8 border-t border-[#3a3a3a] text-center">
            <Link
              to="/explore"
              className="font-mono text-xs uppercase tracking-widest text-[#d0d0d0] hover:text-[#cfff04]"
              data-testid="exit-to-explore"
            >
              ← Browse other coins instead
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
